import {
  ArrowClockwiseIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  SlidersHorizontalIcon,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { FALLBACK_LIMITS, useConfig } from '@entities/config/api';
import { useConfirmSearch } from '@entities/search-run';
import {
  useDeleteSearchSession,
  useSearchSession,
  useSendChatMessage,
} from '@entities/search-session';
import { ChatPanel, chatErrorKey, isNetworkError, useChatDraft } from '@features/chat';
import { CriteriaFallbackForm, CriteriaPanel, readinessPercent } from '@features/criteria';
import { DeleteSearchDialog } from '@features/searches';
import { ApiError } from '@shared/api/problem';
import { ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { randomUuid } from '@shared/lib/uuid';
import { Button, ButtonLink, Container, Skeleton, StateBlock } from '@shared/ui';
import { Modal } from '@shared/ui/Modal/Modal';

import styles from './AssistantPage.module.css';

/**
 * Экран AI-подбора конкретного запроса (FE-070…FE-073, FE-151; MVP_PRD §9, §20).
 *
 * Страница только собирает: диалог, панель критериев и резервную форму. Ни
 * одного решения о подборе здесь нет — следующий вопрос выбирает планировщик,
 * готовность считает backend, тексты собираются из ключей.
 *
 * Оркестровка сводится к трём правилам:
 *
 * * **запрос приходит из адреса.** `/app/searches/:searchId/assistant` — это
 *   конкретный запрос, а не «текущий»: у пользователя их несколько, и открывать
 *   «какой-нибудь» означало бы показать чужой диалог (AC-07). Создаёт запросы
 *   список `/app`, эта страница — никогда;
 * * **первичный запрос не теряется.** Текст, с которым запрос был создан
 *   (`initial_prompt`), уходит первым сообщением диалога — иначе критерии из
 *   него никто не извлечёт (AC-01);
 * * **неотправленное возвращается в поле, а не висит в ленте** (FE-582).
 *   Сообщение, не дошедшее до сервера, откатывается целиком, текст снова в
 *   поле ввода; отдельной кнопки повтора нет — отправить его можно тем же
 *   способом, что и любое другое.
 */
export function AssistantPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { searchId = '' } = useParams();
  const { data: config } = useConfig();
  const maxLength = (config?.limits ?? FALLBACK_LIMITS).chat_message_max_length;

  const sessionQuery = useSearchSession(searchId || null);
  const deleteSession = useDeleteSearchSession();

  const state = sessionQuery.data ?? null;
  const sessionId = state?.session.id ?? null;

  const sendMessage = useSendChatMessage(sessionId);
  const confirmSearch = useConfirmSearch();
  const draft = useChatDraft(sessionId);

  const [sheetOpen, setSheetOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  const autoSent = useRef(false);

  /**
   * Подтверждение критериев запускает подбор и ведёт на экран результатов.
   *
   * `POST /search-sessions/{id}/confirm` отвечает `202`: прогон только
   * поставлен в очередь, поэтому переход идёт на `/app/results?run={id}`, где
   * опрашивается статус и показываются этапы (FE-080). Идентификатор прогона
   * попадает в адрес сразу — ссылка воспроизводима, а перезагрузка вкладки во
   * время ожидания ничего не теряет.
   *
   * Отдельного экрана «проверьте критерии» нет: тот же список с метками виден
   * в панели рядом с диалогом.
   */
  const confirmCriteria = useCallback(() => {
    const criteriaVersionId = state?.criteria.version_id;
    if (!sessionId || criteriaVersionId === undefined) return;
    confirmSearch.mutate(
      { sessionId, criteriaVersionId },
      {
        onSuccess: (accepted) => {
          setSheetOpen(false);
          void navigate(
            `${ROUTES.searchResults(sessionId)}?run=${encodeURIComponent(accepted.search_run_id)}`,
          );
        },
      },
    );
  }, [confirmSearch, navigate, sessionId, state]);

  const send = useCallback(
    (content: string, quickReplyCodes?: string[]) => {
      const clientMessageId = randomUuid();
      draft.clear();
      sendMessage.mutate(
        { clientMessageId, content, quickReplyCodes },
        {
          // Сообщение не дошло до сервера: набранный текст возвращается в
          // поле — ошибка обещает «Ihre Eingabe bleibt erhalten» (решение
          // заказчика от 22.10.2026). Подсказки тоже были текстом поля
          // (FE-595), и вместе с ним возвращаются нажатыми.
          onError: (error) => {
            if (isNetworkError(error)) draft.restore(content);
          },
        },
      );
    },
    [draft, sendMessage],
  );

  /**
   * Первичный запрос уходит первым сообщением диалога (AC-01, AC-02).
   *
   * Источник — `initial_prompt` **с сервера**, а не `sessionStorage`: текст с
   * главной потребляет список `/app` при создании запроса, и к моменту открытия
   * диалога в хранилище его уже нет. Раньше отсюда читалось хранилище, и
   * сообщение не отправлялось вовсе — запрос оставался пустым, а критерии из
   * фразы пользователя никто не извлекал.
   *
   * Тем же путём уходит текст, набранный в форме нового запроса на `/app`:
   * оба способа создают запрос одинаково, и вести себя дальше они обязаны
   * одинаково.
   *
   * Условие «диалог пуст» — единственный признак, что сообщение ещё не
   * отправлено: `initial_prompt` остаётся на запросе навсегда, и повторной
   * отправки после первого сообщения быть не может.
   */
  useEffect(() => {
    if (!state || state.messages.length > 0 || autoSent.current || sendMessage.isPending) return;
    const prompt = state.session.initial_prompt?.trim();
    if (!prompt) return;
    autoSent.current = true;
    send(prompt);
  }, [send, sendMessage.isPending, state]);

  /**
   * Удаление запроса целиком (MVP_PRD §20).
   *
   * Раньше здесь был «сброс», который архивировал подбор и сразу заводил новый;
   * с мультипоиском это два разных действия, и оба принадлежат списку. Отсюда
   * остаётся удаление текущего запроса с возвратом в список.
   */
  /**
   * Оболочка: шаг трекера (FE-192).
   *
   * **Рельса Karl на этом экране нет** (`karl` не передаётся). Диалог с Karl —
   * и есть содержимое страницы: рельс рядом с ним давал бы две ленты и два
   * поля ввода для одной переписки, то есть дубль основного действия
   * (DESIGN_SYSTEM §5.1), и забирал бы ширину у настоящего диалога.
   *
   * Страница сообщает **состояние**, а не рисует навигацию: где окажется
   * трекер, решает `AppLayout`.
   */
  useAppShell({
    // Стадия приходит с backend полем `stage` (BE-200): выводить её из
    // статуса прогона больше не нужно и нельзя — это разные состояния.
    stage: state?.session.stage ?? null,
    paid: state?.session.paid ?? false,
  });

  const confirmReset = () => {
    if (!sessionId) return;
    deleteSession.mutate(sessionId, {
      onSuccess: () => {
        setResetOpen(false);
        setSheetOpen(false);
        void navigate(ROUTES.app, { replace: true });
      },
    });
  };

  // --- Состояния загрузки и ошибки ------------------------------------------

  if (sessionQuery.isPending) {
    return (
      <Container>
        <div className={styles.loading}>
          <Skeleton height="2.5rem" width="16rem" />
          <div className={styles.loadingGrid}>
            <Skeleton height="26rem" radius="var(--radius-card)" />
            <Skeleton height="26rem" radius="var(--radius-card)" />
          </div>
        </div>
      </Container>
    );
  }

  if (sessionQuery.isError || state === null) {
    // `404` — запрос удалён или принадлежит другому пользователю (§27).
    // Это не сбой, и предлагать «Повторить» здесь бессмысленно.
    const notFound = sessionQuery.error instanceof ApiError && sessionQuery.error.status === 404;
    return (
      <Container>
        <div className={styles.stateWrap}>
          <StateBlock
            tone={notFound ? 'neutral' : 'error'}
            title={notFound ? t('searches.notFound.title') : t('assistant.error.title')}
            description={
              notFound ? t('searches.notFound.text') : t(chatErrorKey(sessionQuery.error))
            }
            action={
              notFound ? (
                <ButtonLink to={ROUTES.app} variant="secondary">
                  {t('searches.backToList')}
                </ButtonLink>
              ) : (
                <Button
                  variant="secondary"
                  iconEnd={<ArrowClockwiseIcon size={16} weight="bold" />}
                  onClick={() => {
                    void sessionQuery.refetch();
                  }}
                >
                  {t('error.retry')}
                </Button>
              )
            }
          />
        </div>
      </Container>
    );
  }

  // --- Диалог (FE-070) + панель критериев (FE-071) --------------------------

  // Слот живёт до следующего сообщения: подтверждение сохранения показывает
  // сама форма, и вырывать её из-под пользователя нельзя.
  const showFallback = sendMessage.data?.fallback_required === true;
  // Тот же процент, что в панели: кнопка шторки и панель рядом не имеют права
  // расходиться на единицу из-за разного округления.
  const readiness = readinessPercent(state.criteria);
  const confirmError = confirmSearch.isError ? t(chatErrorKey(confirmSearch.error)) : undefined;

  return (
    <Container>
      <Link to={ROUTES.app} className={styles.back}>
        <ArrowLeftIcon size={16} weight="bold" aria-hidden="true" />
        {t('searches.backToList')}
      </Link>

      <div className={styles.toolbar}>
        <div>
          {/* Заголовок — название запроса: у пользователя их несколько, и
              «AI-подбор» на всех одинаково не отвечает на вопрос «какой». */}
          <h1 className={styles.title}>{state.session.title ?? t('searches.untitled')}</h1>
          <p className={styles.subtitle}>{t('assistant.subtitle')}</p>
        </div>
        <div className={styles.toolbarActions}>
          <Button
            variant="secondary"
            className={styles.criteriaButton}
            aria-haspopup="dialog"
            onClick={() => {
              setSheetOpen(true);
            }}
            iconEnd={<SlidersHorizontalIcon size={16} weight="bold" />}
          >
            {t('assistant.openCriteria', { percent: readiness })}
          </Button>
          <Button
            variant="ghost"
            onClick={() => {
              setResetOpen(true);
            }}
          >
            {t('searches.action.delete')}
          </Button>
        </div>
      </div>

      <div className={styles.workspace}>
        <div className={styles.chatColumn}>
          <ChatPanel
            session={state}
            draft={draft}
            maxLength={maxLength}
            sending={sendMessage.isPending}
            sendError={sendMessage.error}
            onSend={send}
            fallbackSlot={
              showFallback ? (
                <CriteriaFallbackForm sessionId={state.session.id} criteria={state.criteria} />
              ) : undefined
            }
            actionSlot={
              // До 1024 px панели критериев рядом нет, а в шторке «Meine
              // Kriterien» запуск подбора не найти: кнопка стоит прямо над
              // полем ввода, как только критерии собраны (FE-595). С 1024 px
              // она скрыта — запуск там в панели справа.
              state.criteria.ready_for_confirmation ? (
                <div className={styles.inlineConfirm}>
                  {confirmError !== undefined ? (
                    <p className={styles.confirmError} role="alert">
                      {confirmError}
                    </p>
                  ) : null}
                  <Button
                    fullWidth
                    disabled={confirmSearch.isPending}
                    onClick={confirmCriteria}
                    iconEnd={<ArrowRightIcon size={16} weight="bold" />}
                  >
                    {confirmSearch.isPending
                      ? t('criteria.panel.confirming')
                      : t('criteria.panel.confirm')}
                  </Button>
                </div>
              ) : undefined
            }
          />
        </div>

        <aside className={styles.criteriaColumn}>
          <CriteriaPanel
            sessionId={state.session.id}
            criteria={state.criteria}
            onConfirm={confirmCriteria}
            confirmPending={confirmSearch.isPending}
            confirmError={confirmError}
          />
        </aside>
      </div>

      <Modal
        open={sheetOpen}
        onClose={() => {
          setSheetOpen(false);
        }}
        labelledBy="assistant-criteria-sheet"
        className={styles.sheet}
      >
        {sheetOpen ? (
          <>
            <h2 id="assistant-criteria-sheet" className="visually-hidden">
              {t('criteria.panel.title')}
            </h2>
            {/* До 1024 px панель живёт только в шторке, и подтверждение
                запускается отсюда. Без признака отправки кнопка оставалась
                активной и молчала, пока запуск шёл (а он ждёт перечитывания
                заказа); без текста ошибки отказ backend не видел никто —
                колонка, где он показывался, на узком экране скрыта. */}
            <CriteriaPanel
              sessionId={state.session.id}
              criteria={state.criteria}
              onConfirm={confirmCriteria}
              confirmPending={confirmSearch.isPending}
              confirmError={confirmError}
            />
          </>
        ) : null}
      </Modal>

      <DeleteSearchDialog
        open={resetOpen}
        title={state.session.title}
        pending={deleteSession.isPending}
        onCancel={() => {
          setResetOpen(false);
        }}
        onConfirm={confirmReset}
      />
    </Container>
  );
}
